import React from 'react';
import { QueryHookOptions } from '@apollo/client';
import { OperationData, OperationVariables, gql } from '@ts-gql/tag';
import { useApolloClient, useQuery } from '@ts-gql/apollo';

export const GET_ENTITIES_QUERY = gql`
  query GetEntities($clientId: String!) {
    getEntities(clientId: $clientId) {
      cloudId
      name
      endDateUtc
      entityTypeId
      isDeleted
      familyGroupId
    }
  }
` as import('../../__generated__/ts-gql/GetEntities').type;

export type GetEntitiesQueryVariables = OperationVariables<
  typeof GET_ENTITIES_QUERY
>;
export type GetEntitiesQueryResult = OperationData<typeof GET_ENTITIES_QUERY>;

export const useGetEntities = (
  options: QueryHookOptions<GetEntitiesQueryResult, GetEntitiesQueryVariables>
) => {
  const variables = options.variables as GetEntitiesQueryVariables;

  const res = useQuery(GET_ENTITIES_QUERY, {
    context: { service: 'clients' },
    variables,
    ...options,
  });
  const { data } = res;
  return React.useMemo(
    () => ({ ...res, data: data ? processData(data) : null }),
    [res, data]
  );
};

export const useGetEntitiesAsync = () => {
  const apolloClient = useApolloClient();

  return React.useCallback(
    async (variables: GetEntitiesQueryVariables) => {
      const options = {
        context: { service: 'clients' },
        query: GET_ENTITIES_QUERY,
        variables,
      };
      const res = await apolloClient.query<typeof GET_ENTITIES_QUERY>(options);
      const { data } = res;
      return {
        ...res,
        data: data ? processData(data) : null,
      };
    },
    [apolloClient]
  );
};

export function processData(data: GetEntitiesQueryResult) {
  const result: GetEntitiesQueryResult['getEntities'] | undefined | null =
    data?.getEntities?.map((raw) => ({
      ...raw,
      endDateUtc: raw.endDateUtc && new Date(raw.endDateUtc),
    }));
  return result;
}
